/* =============================================================================
   EDGE-FUNCTIONS — Auth-aware Single-Source-Wrapper (Session 68)
   =============================================================================
   Zentrale Schnittstelle für ALLE Aufrufe an Supabase Edge Functions.
   Ersetzt verstreute `supa.functions.invoke(...)`-Calls durch ein Modul, das
   vor jedem Call die Session prüft und bei drohendem Token-Ablauf proaktiv
   `refreshSession()` aufruft.

   PROBLEM:
     supabase-js v2 erneuert das JWT zwar automatisch via einen internen
     `setTimeout`, der ca. 60 s vor `expires_at` feuert. Bei einem Browser-Tab,
     der über Stunden im Hintergrund liegt (Throttling) oder bei einem Gerät,
     das im Standby war, kann dieser Timer aber „durchschlafen". Resultat:
     `tse-sign` etc. erhalten ein abgelaufenes JWT und antworten 401
     („Auth-Kontext fehlt"). Im Pilot-Betrieb ist das fatal — Bons gehen
     beim Kassieren verloren, Operator kann das nicht selbst diagnostizieren.

   LÖSUNG:
     - Vor jedem Invoke `getSession()` (synchroner Cache-Hit) lesen.
     - Wenn `expires_at - now < REFRESH_THRESHOLD_MS` → `refreshSession()`.
     - Parallele Calls deduplizieren über `_refreshInFlight`-Promise.
     - Bei 401-Response trotzdem: einmaliger forcierter Refresh + Retry.

   NICHT-Verantwortlichkeiten:
     - Re-Login bei abgelaufenem Refresh-Token (das ist Aufgabe von
       `bootstrap.js`/Login-View — wir loggen und geben den Error zurück).
     - Tenant-/Permission-Checks (server-seitig im Edge-Function-Handler).

   AUFRUFER (Stand S68):
     - src/core/tse-adapter.js          → 'tse-sign'        (KRITISCH)
     - src/core/delivery-routing.js     → 'uber-direct-create-delivery'
                                         'uber-update-delivery-status'
                                         (S66 No-Op, bleibt zukunftsfest)
     - src/views/admin/settings-delivery-rules.js
                                        → 'uber-auth'
     - src/views/admin/settings-compliance-tse.js
                                        → 'tse-provision'

   PATTERN-VERWANDT: src/core/tse-adapter.js, src/core/gdpr.js
   ========================================================================== */

/** Restzeit-Schwelle: unter 5 Min wird vor dem Invoke refresht. */
const REFRESH_THRESHOLD_MS = 5 * 60 * 1000;

/** Promise eines aktuell laufenden refreshSession-Calls, oder null.
 *  Dedupe — wenn KDS, Theke und Cockpit gleichzeitig invoken, soll nur
 *  EIN Refresh-Call rausgehen, alle anderen warten auf das Ergebnis. */
let _refreshInFlight = null;

/** Memo des letzten `expires_at` für das wir bereits refresht haben — verhindert
 *  Refresh-Sturm wenn ensureFreshSession() im selben Lauf mehrfach feuert. */
let _lastRefreshedExpiresAt = 0;

/* -------------------------------------------------------------------------- */
/* Internals                                                                   */
/* -------------------------------------------------------------------------- */

function getSupa() {
  const supa = (typeof window !== 'undefined') && window.App && window.App.supabase;
  if (!supa) throw new Error('Supabase-Client nicht initialisiert.');
  return supa;
}

/** Liest die aktuelle Session aus dem supabase-js Cache.
 *  Macht KEINEN Netzwerk-Call (es sei denn der Cache ist leer). */
async function _readSession(supa) {
  try {
    const r = await supa.auth.getSession();
    return (r && r.data && r.data.session) || null;
  } catch (e) {
    console.warn('[edge-functions] getSession failed:', e && e.message);
    return null;
  }
}

/** Erzwingt einen `refreshSession`-Call, dedupliziert parallele Aufrufe.
 *  Liefert die neue Session oder null bei Fehler. */
async function _doRefresh(supa) {
  if (_refreshInFlight) return _refreshInFlight;
  _refreshInFlight = (async () => {
    try {
      const r = await supa.auth.refreshSession();
      if (r && r.error) {
        console.warn('[edge-functions] refreshSession error:', r.error.message);
        return null;
      }
      const session = r && r.data && r.data.session;
      if (session && session.expires_at) {
        _lastRefreshedExpiresAt = session.expires_at;
      }
      return session || null;
    } catch (e) {
      console.warn('[edge-functions] refreshSession threw:', e && e.message);
      return null;
    } finally {
      _refreshInFlight = null;
    }
  })();
  return _refreshInFlight;
}

/** Pre-Check: refresht proaktiv, wenn die Restzeit unter dem Schwellwert liegt.
 *  Wenn keine Session existiert (User nicht eingeloggt), passiert nichts —
 *  der Invoke wird ohne Auth-Header rausgehen und der Edge-Function-Handler
 *  liefert 401, was der Caller normal behandelt. */
async function _ensureFreshSession(supa) {
  const session = await _readSession(supa);
  if (!session) return;
  const expiresAt = Number(session.expires_at || 0); // sekunden seit epoch
  if (!expiresAt) return;
  // Bereits frisch refresht — kein zweiter Call pro Lauf.
  if (expiresAt === _lastRefreshedExpiresAt) return;
  const remainingMs = (expiresAt * 1000) - Date.now();
  if (remainingMs > REFRESH_THRESHOLD_MS) return;
  await _doRefresh(supa);
}

/** Identifiziert Auth/401-Fehler aus dem Invoke-Response.
 *  supabase-js verpackt 401 als `FunctionsHttpError` mit
 *  `error.context.response.status === 401`. */
export function _looksLikeAuthError(error) {
  if (!error) return false;
  const ctx = error.context;
  if (ctx && ctx.response && typeof ctx.response.status === 'number') {
    if (ctx.response.status === 401 || ctx.response.status === 403) return true;
  }
  const msg = String(error.message || '').toLowerCase();
  if (msg.includes('auth') && (msg.includes('missing') || msg.includes('fehlt') || msg.includes('expired'))) {
    return true;
  }
  if (msg.includes('jwt') && msg.includes('expired')) return true;
  return false;
}

/* -------------------------------------------------------------------------- */
/* Public API                                                                  */
/* -------------------------------------------------------------------------- */

/**
 * Auth-aware Wrapper um `supabase.functions.invoke()`.
 *
 *   - Pre-Check: refresht Session wenn `expires_at - now < 5 min`.
 *   - Bei 401/403 im Response: einmaliger forcierter Refresh + Retry.
 *   - Returnt {data, error} exakt wie der native Aufruf — Caller-Code
 *     muss nur den Import-Pfad ändern, kein Branching.
 *
 * @param {string} name      Edge-Function-Name (z.B. 'tse-sign')
 * @param {object} [body]    Body-Payload (wird als JSON serialisiert)
 * @param {object} [opts]    Reserved (z.B. headers künftig)
 * @returns {Promise<{data: any, error: any}>}
 */
export async function safeInvoke(name, body, opts = {}) {
  if (!name || typeof name !== 'string') {
    return { data: null, error: new Error('safeInvoke: name fehlt') };
  }
  const supa = getSupa();
  await _ensureFreshSession(supa);
  const invokeArgs = { body: body == null ? {} : body };
  let result = await supa.functions.invoke(name, invokeArgs);
  if (result && result.error && _looksLikeAuthError(result.error) && !opts._retried) {
    console.info('[edge-functions]', name, '→ 401/auth, force-refresh + retry');
    await _doRefresh(supa);
    result = await supa.functions.invoke(name, invokeArgs);
    // Mark als retry, damit kein Endlos-Loop entstehen kann.
    if (result && result.error) result._retried = true;
  }
  return result;
}

/* -------------------------------------------------------------------------- */
/* Test-Hooks                                                                  */
/* -------------------------------------------------------------------------- */

/** Reset interner State — nur für Vitest. */
export function _resetInternalState() {
  _refreshInFlight = null;
  _lastRefreshedExpiresAt = 0;
}

export const _internals = Object.freeze({
  REFRESH_THRESHOLD_MS,
  _readSession,
  _doRefresh,
  _ensureFreshSession,
  _looksLikeAuthError
});
